import { useLang } from 'hooks/lang';

import { actions, ProjectTag } from 'entities';

import { Button, Modal, Row, LabeledInput, Column, Label, Tag } from 'components/Blocks';
import { SaveIcon, XIcon } from 'components/Icons';

import { useFormControl } from 'hooks';
import { useTeamProfile } from '../context';
import { useToast } from 'providers/Toast';
import { omitUndefined } from 'utils/object';
import { validateTag } from './utils';

interface ProjectTagFormProps {
  children?: never;
  close: () => void;
  projectTag: ProjectTag | null;
}

const defaultTag = {
  name: '',
  color: '',
};

export function ProjectTagForm({ close, projectTag }: ProjectTagFormProps) {
  const control = useFormControl<ProjectTag>(projectTag ?? defaultTag, { watch: true });

  const { t } = useLang();

  const { addToast } = useToast();

  const { team, updateTeam } = useTeamProfile();

  const createProjectTag = actions.teams.createProjectTag.use({
    onSuccess: (newTag) => {
      updateTeam((old) => ({
        ...old,
        projectTags: [...old.projectTags, newTag],
      }));

      close();
    },
  });

  const updateProjectTag = actions.teams.updateProjectTag.use({
    onSuccess: (mutatedTag) => {
      updateTeam((old) => ({
        ...old,
        projectTags: old.projectTags.map((tag) => (tag.id === mutatedTag.id ? mutatedTag : tag)),
      }));

      close();
    },
  });

  const loading = createProjectTag.loading || updateProjectTag.loading;

  const saveNewTag = (): void => {
    const { name, id, color } = control.state;

    const { projectTags } = team;

    const tagToValidate = {
      id,
      name,
      color,
      projectTags,
    };

    const fillingError = validateTag(tagToValidate, t);

    if (fillingError) return addToast(fillingError);

    const isUpdate = !!id;

    const mutation = isUpdate ? updateProjectTag : createProjectTag;

    mutation.execute(
      omitUndefined({
        name,
        color,
        id,
        team: team.id,
      }),
    );
  };

  return (
    <Modal
      isOpen={!!projectTag}
      close={close}
      title={(projectTag?.id ? t('edit-tag') : t('create-tag')) as string}
      gap="8"
      className="w-full max-w-md"
    >
      <LabeledInput
        required
        name="name"
        label={t('name')}
        value={control.state?.name}
        onChange={control.handleChange}
      />

      <Column gap="2">
        <Label>Color</Label>

        <input
          name="color"
          type="color"
          className="h-9 w-full"
          value={control.state?.color}
          onChange={control.handleChange}
        />
      </Column>

      <Row gap="2" justifyContent="space-between">
        <Label>Preview</Label>

        <Tag
          name={control.state?.name || '...'}
          color={control.state?.color}
          className="w-fit"
          size="medium"
        />
      </Row>

      <Row justifyContent="space-between">
        <Button variant="outlined" onClick={close}>
          <XIcon />
          {t('close')}
        </Button>

        <Button onClick={saveNewTag} loading={loading}>
          <SaveIcon />
          {t('save')}
        </Button>
      </Row>
    </Modal>
  );
}
